import {
  CLIENT_INTAKE_SUBMITTED_BODY,
  CLIENT_INTAKE_SUBMITTED_HEADLINE,
  PRIMARY_EVIDENCE_TYPE_OPTIONS,
  toPrimaryEvidenceTypeLabel,
} from "@enrein/shared-contracts/display/tier1-client-display";
import type { PrimaryEvidenceType } from "@enrein/shared-contracts";
import type { ClientPortalApiClient } from "../../../api.js";
import { useV10IntakeForm } from "../adapters/v10-use-intake-form.js";
export function V10IntakeTab({
  active,
  api,
  linkId,
  sessionToken,
  linkStatus,
  onStatus,
}: {
  readonly active: boolean;
  readonly api: ClientPortalApiClient;
  readonly linkId: string;
  readonly sessionToken: string;
  readonly linkStatus: string;
  readonly onStatus: (message: string | null) => void;
}) {
  const intake = useV10IntakeForm({ api, linkId, sessionToken, linkStatus, onStatus });

  if (!active) return null;

  if (intake.error) {
    return (
      <div className="v10-cl-tab-panel" id="v10-tab-intake" role="tabpanel" aria-labelledby="v10-tab-btn-intake">
        <p className="v10-cl-kicker">Getting started</p>
        <h2 className="v10-cl-headline">Business intake</h2>
        <p className="v10-cl-lede v10-cl-intake-error">{intake.error}</p>
      </div>
    );
  }

  if (!intake.view) {
    return (
      <div
        className="v10-cl-tab-panel enrein-loading-line"
        id="v10-tab-intake"
        role="tabpanel"
        aria-labelledby="v10-tab-btn-intake"
        aria-busy="true"
      >
        <p className="v10-cl-kicker">Getting started</p>
        <h2 className="v10-cl-headline">Business intake</h2>
        <p className="v10-cl-lede">Loading your intake form…</p>
      </div>
    );
  }

  if (intake.readOnly) {
    return (
      <div className="v10-cl-tab-panel" id="v10-tab-intake" role="tabpanel" aria-labelledby="v10-tab-btn-intake">
        <div className="v10-cl-intake-intro">
          <div>
            <p className="v10-cl-kicker">Getting started</p>
            <h2 className="v10-cl-headline">{CLIENT_INTAKE_SUBMITTED_HEADLINE}</h2>
          </div>
        </div>
        <div className="v10-cl-intake-submitted" role="status">
          <strong>Submitted to your agency</strong>
          <p>{CLIENT_INTAKE_SUBMITTED_BODY}</p>
        </div>
        <dl className="v10-cl-fact-grid">
          <div className="v10-cl-fact">
            <small>Business name</small>
            <b>{intake.view.businessName}</b>
          </div>
          {intake.view.primaryPhone ? (
            <div className="v10-cl-fact">
              <small>Phone</small>
              <b>{intake.view.primaryPhone}</b>
            </div>
          ) : null}
          {intake.view.primaryEmail ? (
            <div className="v10-cl-fact">
              <small>Email</small>
              <b>{intake.view.primaryEmail}</b>
            </div>
          ) : null}
          {intake.view.services.length > 0 ? (
            <div className="v10-cl-fact">
              <small>Services</small>
              <b>{intake.view.services.map((s: { name: string }) => s.name).join(", ")}</b>
            </div>
          ) : null}
          {intake.view.serviceAreas.length > 0 ? (
            <div className="v10-cl-fact">
              <small>Service areas</small>
              <b>{intake.view.serviceAreas.map((a: { name: string }) => a.name).join(", ")}</b>
            </div>
          ) : null}
          {intake.view.primaryEvidenceType ? (
            <div className="v10-cl-fact">
              <small>Primary evidence</small>
              <b>{toPrimaryEvidenceTypeLabel(intake.view.primaryEvidenceType)}</b>
            </div>
          ) : null}
          {intake.view.description ? (
            <div className="v10-cl-fact v10-cl-fact--wide">
              <small>Description</small>
              <b>{intake.view.description}</b>
            </div>
          ) : null}
        </dl>
      </div>
    );
  }

  return (
    <div className="v10-cl-tab-panel" id="v10-tab-intake" role="tabpanel" aria-labelledby="v10-tab-btn-intake">
      <div className="v10-cl-intake-intro">
        <div>
          <p className="v10-cl-kicker">Getting started</p>
          <h2 className="v10-cl-headline">Tell us about your business</h2>
          <p className="v10-cl-lede">
            Share the basics your agency needs to start your website. Your progress saves automatically so you can
            return anytime before you submit.
          </p>
          {intake.draftSaveState === "saved" ? (
            <p className="v10-cl-muted" role="status">
              Draft saved
            </p>
          ) : null}
          {intake.draftSaveState === "error" ? (
            <p className="v10-cl-intake-error" role="status">
              {intake.draftSaveError ?? "Could not save draft — check your connection and try again."}
            </p>
          ) : null}
        </div>
      </div>
      <div className="v10-cl-intake-form">
        <label className="v10-cl-field" htmlFor="v10-intake-business-name">
          <span>Business name</span>
          <input
            id="v10-intake-business-name"
            value={intake.businessName}
            onChange={(event) => intake.setBusinessName(event.target.value)}
            onBlur={() => intake.flushDraftOnBlur()}
            required
            autoComplete="organization"
          />
        </label>
        <label className="v10-cl-field" htmlFor="v10-intake-phone">
          <span>Primary phone</span>
          <input
            id="v10-intake-phone"
            value={intake.primaryPhone}
            onChange={(event) => intake.setPrimaryPhone(event.target.value)}
            onBlur={() => intake.flushDraftOnBlur()}
            autoComplete="tel"
          />
        </label>
        <label className="v10-cl-field" htmlFor="v10-intake-email">
          <span>Primary email</span>
          <input
            id="v10-intake-email"
            type="email"
            value={intake.primaryEmail}
            onChange={(event) => intake.setPrimaryEmail(event.target.value)}
            onBlur={() => intake.flushDraftOnBlur()}
            autoComplete="email"
          />
        </label>
        <label className="v10-cl-field v10-cl-field--wide" htmlFor="v10-intake-services">
          <span>Services (one per line)</span>
          <textarea
            id="v10-intake-services"
            value={intake.servicesText}
            onChange={(event) => intake.setServicesText(event.target.value)}
            onBlur={() => intake.flushDraftOnBlur()}
            placeholder={"HVAC repair\nEmergency service"}
          />
        </label>
        <label className="v10-cl-field v10-cl-field--wide" htmlFor="v10-intake-service-areas">
          <span>Service areas (one per line)</span>
          <textarea
            id="v10-intake-service-areas"
            value={intake.serviceAreasText}
            onChange={(event) => intake.setServiceAreasText(event.target.value)}
            onBlur={() => intake.flushDraftOnBlur()}
            placeholder={"Austin\nRound Rock"}
          />
        </label>
        <label className="v10-cl-field" htmlFor="v10-intake-evidence">
          <span>Primary evidence type</span>
          <select
            id="v10-intake-evidence"
            value={intake.primaryEvidenceType}
            onChange={(event) => intake.setPrimaryEvidenceType(event.target.value as PrimaryEvidenceType | "")}
            onBlur={() => intake.flushDraftOnBlur()}
          >
            <option value="">Select evidence type (optional)</option>
            {PRIMARY_EVIDENCE_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="v10-cl-field v10-cl-field--wide" htmlFor="v10-intake-evidence-files">
          <span>Supporting files (photos, PDFs)</span>
          <input
            id="v10-intake-evidence-files"
            type="file"
            multiple
            accept="image/*,.pdf"
            onChange={(event) => {
              intake.attachEvidenceFiles(event.target.files);
              event.target.value = "";
            }}
          />
          {intake.evidenceFiles.length > 0 ? (
            <ul className="v10-cl-intake-file-list">
              {intake.evidenceFiles.map((file) => (
                <li key={file.fileId}>
                  {file.fileName} ({Math.round(file.sizeBytes / 1024)} KB)
                </li>
              ))}
            </ul>
          ) : (
            <p className="v10-cl-muted">Attach portfolio photos or PDFs — metadata is saved with your draft.</p>
          )}
        </label>
        <label className="v10-cl-field v10-cl-field--wide" htmlFor="v10-intake-description">
          <span>Short description</span>
          <textarea
            id="v10-intake-description"
            value={intake.description}
            onChange={(event) => intake.setDescription(event.target.value)}
            onBlur={() => intake.flushDraftOnBlur()}
            placeholder="A few sentences about your business."
          />
        </label>
      </div>
      <div className="v10-cl-actions">
        <button
          type="button"
          className="v10-cl-btn v10-cl-btn--gold"
          onClick={() => void intake.submit()}
          disabled={intake.submitting || intake.businessName.trim().length === 0}
        >
          {intake.submitting ? "Submitting…" : "Submit intake"}
        </button>
      </div>
    </div>
  );
}
